import {useEffect, useState} from 'react'
import {deviceCategoryFor, isModernIPadUserAgent, type DeviceCategory} from 'notability-services-common-universal'

// See Decision Document: https://docs.google.com/document/d/1BWZciO1nIvZ0jRL1dP0KsOjGl6RxQmUz9UbLy-EOu10/edit?tab=t.0#heading=h.sj20fxc4g8q2
const mimeTypePriorityList = ['audio/mp4', 'audio/webm;codecs=opus'] as const

const appStoreHref = 'https://apps.apple.com/app/notability/id360593530'
const appStoreHrefNative = 'itms-apps://apps.apple.com/app/notability/id360593530'

function userAgentIsIOS12OrOlder(agentString?: string) {
  return agentString ? /iPad|iPhone|iPod/.test(agentString) : false
}

/**
 * Returns true when the client device is iOS.  Returns false on the server.
 */
export const useIsIOS = (): boolean => {
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    setIsIOS(userAgentIsIOS12OrOlder(navigator.userAgent ?? navigator.vendor) || navigator.maxTouchPoints > 1) // maxTouchPoints detects iOS 13+
  }, [])

  return isIOS
}

function userAgentIsApple(agentString?: string) {
  return agentString ? /iPad|iPhone|iPod|Macintosh/.test(agentString) : false
}

/**
 * Returns true when the client device is apple.  Returns false on the server.
 */
export const useIsAppleDevice = (): boolean => {
  const [isApple, setIsApple] = useState(false)

  useEffect(() => {
    setIsApple(userAgentIsApple(navigator.userAgent ?? navigator.vendor))
  }, [])

  return isApple
}

/**
 * Returns the app store link that will work best on the client platform.
 */
export function useAppStoreLink(): string {
  const isApple = useIsAppleDevice()
  return isApple ? appStoreHrefNative : appStoreHref
}

export type Browser = 'Chrome' | 'Firefox' | 'Safari' | 'Opera' | 'Edge'

export function detectBrowser() {
  const {userAgent} = navigator
  if (userAgent.match(/chrome|chromium|crios/i)) {
    return 'Chrome'
  } else if (userAgent.match(/firefox|fxios/i)) {
    return 'Firefox'
  } else if (userAgent.match(/safari/i)) {
    return 'Safari'
  } else if (userAgent.match(/opr\//i)) {
    return 'Opera'
  } else if (userAgent.match(/edg/i)) {
    return 'Edge'
  }
}

export function detectPreferredRecordingMimeType() {
  // Environments without MediaRecorder (SSR, non-media test runs) simply have
  // no supported recording mime type; callers already treat undefined as
  // "recording unsupported". A capability probe must not throw where the
  // API is absent.
  if (typeof MediaRecorder === 'undefined') {
    return undefined
  }

  const browser = detectBrowser()

  /**
   * Safari lies, saying it supports recording webm, then subsequently
   * generates an unplayable webm file.
   */
  if (browser === 'Safari') {
    return 'audio/mp4'
  } else {
    return mimeTypePriorityList.find((mimeType) => MediaRecorder.isTypeSupported(mimeType))
  }
}

export function useDetectBrowser() {
  const [browser, setBrowser] = useState<Browser | undefined>()

  useEffect(() => {
    setBrowser(detectBrowser())
  }, [])

  return browser
}

export type OperatingSystem = 'iOS' | 'iPadOS' | 'Android' | 'Windows' | 'MacOS' | 'Linux'

export function detectOperatingSystem(): OperatingSystem | undefined {
  const {userAgent} = navigator
  if (/Windows/.test(userAgent)) {
    return 'Windows'
  } else if (/iPhone/.test(userAgent)) {
    return 'iOS'
  } else if (/iPad/.test(userAgent) || isModernIPadUserAgent(userAgent, navigator.maxTouchPoints)) {
    // iPadOS 13+ does not contain "iPad" in the user agent string, only the Mac-plus-touch spoof;
    // pre-13 iPads still say "iPad" outright, so both checks are needed.
    // See https://github.com/getsentry/sentry-javascript/issues/12127 and
    // https://en.wikipedia.org/wiki/Safari_%28web_browser%29#iOS/iPadOS
    return 'iPadOS'
  } else if (/Mac/.test(userAgent)) {
    return 'MacOS'
  } else if (/Android/.test(userAgent)) {
    return 'Android'
  } else if (/Linux/.test(userAgent)) {
    return 'Linux'
  }
  return undefined
}

/**
 * The form factor of this device, for the `Device Category` analytics super-property.
 */
export function detectDeviceCategory(): DeviceCategory {
  return deviceCategoryFor(navigator.userAgent, navigator.maxTouchPoints)
}

export function useDetectOperatingSystem() {
  const [operatingSystem, setOperatingSystem] = useState<OperatingSystem | undefined>()

  useEffect(() => {
    setOperatingSystem(detectOperatingSystem())
  }, [])

  return operatingSystem
}
